import React, { useState, useEffect } from "react";
import { Gallery } from "react-grid-gallery";
import type { Image } from "react-grid-gallery";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { getGalleryImages } from "../firebase";
import "./GallerySection.css";

interface GalleryImage {
  id: string;
  url: string;
  fileName: string;
  name: string;
  size: number;
  uploadedAt: string;
  width: number;
  height: number;
  tags?: { value: string; title: string }[];
}

interface CustomImage extends Image {
  original: GalleryImage;
}

const GallerySection: React.FC = () => {
  const [photos, setPhotos] = useState<CustomImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(-1);
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        setLoading(true);
        const imagesData = await getGalleryImages();

        const allPhotos: CustomImage[] = Object.values(imagesData)
          .filter(
            (image: any) =>
              typeof image.url === "string" && image.url.startsWith("http")
          )
          .sort(
            (a, b) =>
              new Date(b.uploadedAt).getTime() -
              new Date(a.uploadedAt).getTime()
          )
          .map((image: any) => {
            if (
              typeof image.width !== "number" ||
              typeof image.height !== "number" ||
              image.width === 0 ||
              image.height === 0
            ) {
              return null;
            }
            const { tags, ...restOfImage } = image;
            return {
              ...restOfImage,
              src: image.url,
              original: image,
            };
          })
          .filter((p): p is CustomImage => p !== null);

        setPhotos(allPhotos);
      } catch (error) {
        console.error("Error fetching photos:", error);
        setPhotos([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPhotos();
  }, []);

  const handleClick = (index: number) => {
    // 라이트박스가 열리기 전에 현재 스크롤 위치 저장
    setScrollPosition(window.scrollY);
    setIndex(index);
  };

  if (loading) {
    return (
      <div className="gallery-section loading">갤러리를 불러오는 중...</div>
    );
  }

  return (
    <section className="gallery-section">
      <div className="gallery-header">
        <h2 className="section-title">갤러리</h2>
      </div>

      {photos.length === 0 ? (
        <div className="no-photos">
          <p>아직 업로드된 사진이 없습니다.</p>
        </div>
      ) : (
        <div className="gallery-container">
          <Gallery
            images={photos}
            onClick={handleClick}
            enableImageSelection={false}
            rowHeight={120}
            margin={2}
          />
        </div>
      )}

      <Lightbox
        slides={photos.map((photo) => ({ src: photo.src }))}
        open={index >= 0}
        index={index}
        noScroll={{ disabled: true }}
        close={() => {
          setIndex(-1);
          // 라이트박스가 완전히 닫힌 후 스크롤 위치 복원
          setTimeout(() => {
            window.scrollTo({
              top: scrollPosition,
              left: 0,
              behavior: "auto",
            });
          }, 0);
        }}
      />
    </section>
  );
};

export default GallerySection;
